/**
 * @typedef ModalSliderDefaults
 * @property {number} closeDuration
 * @property {boolean} closeOnOutsideClick
 * @property {boolean} closeOnEscape
 * @property {string} activeHTMLelementClass
 * @property {string} ariaLabelClose
 * @property {string} closeButtonContent
 * @property {string} previousButtonContent
 * @property {string} nextButtonContent
 * @property {string} modalBackdropClass
 * @property {string} modalClass
 * @property {string} closeButtonClass
 * @property {string} previousButtonClass
 * @property {string} nextButtonClass
 * @property {string} contentContainerClass
 * @property {string} contentClass
 * @property {Object} callbacks
 */

class ModalSlider {
	constructor() {
		this.htmlEl = document.querySelector('html');
		this.backdropEl = null;
		this.modalEl = null;

		this.defaults = {
			closeDuration: 400,
			closeOnOutsideClick: true,
			closeOnEscape: true,

			activeHTMLelementClass: 'modal-active',

			ariaLabelClose: 'Close modal',

			closeButtonContent: '&times;',
			previousButtonContent: '',
			nextButtonContent: '',

			modalBackdropClass: 'modal-backdrop',
			modalClass: 'modal-slider',

			closeButtonClass: 'modal-slider__close',
			previousButtonClass: 'modal-slider__prev',
			nextButtonClass: 'modal-slider__next',

			contentContainerClass: 'modal-slider__content-container',
			contentClass: 'modal-slider__content',

			callbacks: {}
		};

		this.currentOpts = { ...this.defaults };
	}

	setDefaults(defaults = {}) {
		this.defaults = {
			...this.defaults,
			...defaults
		};
	}

	getSetting(settingName, defaultValue = '') {
		return this.currentOpts[settingName] ??
			this.defaults[settingName] ??
			defaultValue;
	}

	initModal() {
		return new Promise((resolve) => {

			this.modalEl = this.createModalEl();
			this.backdropEl = this.createBackdropEl(this.modalEl);

			document.body.appendChild(this.backdropEl);

			this.modalEl
				.querySelector(`.${this.getSetting('closeButtonClass')}`)
				.addEventListener('click', (e) => {
					e.preventDefault();
					this.hide();
				});

			this.modalEl
				.querySelector(`.${this.getSetting('previousButtonClass')}`)
				.addEventListener('click', (e) => {
					e.preventDefault();

					if (typeof this.currentOpts.callbacks?.onPrevious === 'function') {
						this.currentOpts.callbacks.onPrevious();
					}
				});

			this.modalEl
				.querySelector(`.${this.getSetting('nextButtonClass')}`)
				.addEventListener('click', (e) => {
					e.preventDefault();

					if (typeof this.currentOpts.callbacks?.onNext === 'function') {
						this.currentOpts.callbacks.onNext();
					}
				});

			setTimeout(resolve, 0);
		});
	}

	createBackdropEl(modalEl = null) {
		const backdropEl = document.createElement('div');

		backdropEl.className = this.getSetting('modalBackdropClass');

		if (modalEl) {
			backdropEl.appendChild(modalEl);
		}

		return backdropEl;
	}

	createModalEl() {
		const modalEl = document.createElement('div');

		modalEl.className = this.getSetting('modalClass');

		modalEl.innerHTML = `
			<button
				class="${this.getSetting('closeButtonClass')}"
				aria-label="${this.getSetting('ariaLabelClose')}"
			>
				${this.getSetting('closeButtonContent')}
			</button>

			<button
				class="${this.getSetting('previousButtonClass')}"
				type="button"
			>
				${this.getSetting('previousButtonContent')}
			</button>

			<div class="${this.getSetting('contentContainerClass')}">
				<div class="${this.getSetting('contentClass')}"></div>
			</div>

			<button
				class="${this.getSetting('nextButtonClass')}"
				type="button"
			>
				${this.getSetting('nextButtonContent')}
			</button>
		`.trim();

		return modalEl;
	}

	clickOutsideContentListener = (e) => {
		if (
			this.getSetting('closeOnOutsideClick') &&
			e.target.classList.contains(this.getSetting('modalBackdropClass'))
		) {
			this.hide();
		}
	};

	escapeListener = (e) => {
		if (
			this.getSetting('closeOnEscape') &&
			e.keyCode === 27
		) {
			this.hide();
		}
	};

	async show(content = '', opts = {}) {

		this.currentOpts = {
			...this.defaults,
			...opts
		};

		if (this.modalEl) {
			console.error('ModalSlider already active.');
			return;
		}

		await this.initModal();

		this.htmlEl.classList.add(
			this.getSetting('activeHTMLelementClass')
		);

		this.setContent(content);

		this.backdropEl.addEventListener(
			'click',
			this.clickOutsideContentListener
		);

		window.addEventListener(
			'keydown',
			this.escapeListener
		);

		if (typeof this.currentOpts.callbacks?.onShow === 'function') {
			this.currentOpts.callbacks.onShow();
		}
	}

	hide() {

		this.htmlEl.classList.remove(
			this.getSetting('activeHTMLelementClass')
		);

		if (typeof this.currentOpts.callbacks?.onHide === 'function') {
			this.currentOpts.callbacks.onHide();
		}

		this.backdropEl.removeEventListener(
			'click',
			this.clickOutsideContentListener
		);

		window.removeEventListener(
			'keydown',
			this.escapeListener
		);

		setTimeout(() => {

			this.modalEl.remove();
			this.backdropEl.remove();

			this.modalEl = null;
			this.backdropEl = null;

		}, this.currentOpts.closeDuration);
	}

	setContent(content = '') {

		const contentEl = this.modalEl.querySelector(
			`.${this.getSetting('contentClass')}`
		);

		contentEl.innerHTML = content;
	}

	setPreviousDisabled(disabled) {

		this.modalEl
			.querySelector(`.${this.getSetting('previousButtonClass')}`)
			.disabled = disabled;
	}

	setNextDisabled(disabled) {

		this.modalEl
			.querySelector(`.${this.getSetting('nextButtonClass')}`)
			.disabled = disabled;
	}

	getElement() {
		return this.modalEl;
	}
}

export default new ModalSlider();